import { ColumnDef } from "@tanstack/react-table";
import { useForm, useWatch } from "react-hook-form";
import { useSearchParams } from "react-router-dom";
import APIS from "../../constant/Endpoint";
import DataTable from "../Reusable/Datatable";
import NACell from "../Reusable/Datatable/NACell";
import Filter from "./Filter";
import { tw } from "@careinfo/tailwind";
import moment from "moment";
import { useDownload } from "@careinfo/layout";
import Button from "../Reusable/Button";
import { toast } from "react-toastify";
import useGetHook from "../../customHooks/useGetHook";

const PurchaseReturnRegister = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const startOfMonth = moment().startOf("month").format("YYYY-MM-DD");
  const endOfMonth = moment().endOf("month").format("YYYY-MM-DD");
  const checkedAllItems = searchParams.get("selectAllItems") === "1";
  const { download } = useDownload();
  const {
    control,
    register,
    getValues,
    setValue,
    clearErrors,
    formState: { errors },
  } = useForm<any>({
    defaultValues: {
      ids: searchParams.get("ids") || "",
      account: searchParams.get("account") || "",
      user: searchParams.get("user") || "",
      created: searchParams.get("created") || "",
      created__range: `${startOfMonth},${endOfMonth}`,
    },
  });

  const formValues = useWatch({ control });

  const handleIndividualSelect = (value: string) => {
    const clonedParams = new URLSearchParams(searchParams);
    const selectedItems = clonedParams.get("selectedItems")?.split(",");
    if (!selectedItems || !selectedItems === undefined) {
      clonedParams.set("selectedItems", value);
      setSearchParams(clonedParams);
      return;
    }
    const itemIndex = selectedItems?.findIndex((item) => item === value);
    if (itemIndex > -1) {
      selectedItems.splice(itemIndex, 1);
      const newSelectedItems = selectedItems
        ?.map((item) => item)
        .join(",")
        .toString();
      if (newSelectedItems.length) {
        clonedParams.set("selectedItems", newSelectedItems);
      } else {
        clonedParams.delete("selectedItems");
      }
      clonedParams.set("selectAllItems", "0");
    } else {
      selectedItems?.push(value);
      const newSelectedItems = selectedItems
        ?.map((item) => item)
        .join(",")
        .toString();
      clonedParams.set("selectedItems", newSelectedItems);
    }
    setSearchParams(clonedParams);
  };

  const isSelected = (value: string) => {
    const selectedItems = searchParams.get("selectedItems")?.split(",");
    if (!selectedItems) {
      return false;
    }
    return selectedItems.includes(value);
  };

  const handleSelectAll = (table: any) => {
    const {
      options: { data: tableData },
    } = table;
    const clonedParams = new URLSearchParams(searchParams);
    const isSelected = clonedParams.get("selectAllItems");
    if (isSelected === "1") {
      clonedParams.set("selectAllItems", "0");
      clonedParams.delete("selectedItems");
    } else {
      const selectedItems = tableData
        ?.map((item: any) => item.id)
        .join(",")
        .toString();
      clonedParams.set("selectAllItems", "1");
      clonedParams.set("selectedItems", selectedItems);
    }
    setSearchParams(clonedParams);
  };
  const columns: ColumnDef<any, any>[] = [
    {
      id: "select",
      header: ({ table }: any) => {
        return (
          <input
            type="checkbox"
            checked={checkedAllItems}
            onChange={() => handleSelectAll(table)}
            className={tw`cursor-pointer`}
          />
        );
      },
      cell: ({ row }: any) => (
        <input
          type="checkbox"
          checked={isSelected(row?.original?.id)}
          onChange={() => {
            handleIndividualSelect(row?.original?.id);
          }}
          className={tw`cursor-pointer`}
        />
      ),
    },
    {
      accessorKey: "miti",
      header: "Miti",
      cell: ({ row, getValue }: any) => (
        <p className={tw`whitespace-nowrap`}>{getValue()}</p>
      ),
    },
    {
      accessorKey: "seq_no",
      header: "Bill No.",
      cell: NACell,
    },
    {
      accessorKey: "party.full_name",
      header: "Supplier Name",
      cell: NACell,
    },
    {
      accessorKey: "party.vat_pan",
      header: "PAN No.",
      cell: NACell,
    },
    {
      accessorKey: "items",
      header: "Products Name",
      cell: ({ row }) => {
        const names = row.original.items
          .map((item: any) => item.account?.name)
          .filter(Boolean);
        const joinedNames = names.join(", ");

        return (
          <p title={joinedNames} className="truncate cursor-pointer">
            {joinedNames}
          </p>
        );
      },
    },
    {
      accessorKey: "total.items_total",
      header: "Products Amt.",
      cell: NACell,
    },
    {
      accessorKey: "items",
      header: "Unit",
      cell: ({ row }) => {
        const names = row.original.items
          .map((item: any) => item.account?.unit?.name)
          .filter(Boolean);
        const joinedNames = names.join(", ");

        return (
          <p title={joinedNames} className="truncate cursor-pointer">
            {joinedNames}
          </p>
        );
      },
    },
    {
      accessorKey: "total.total_amount",
      header: "Purchase Ret. Amt.",
      cell: NACell,
    },
    {
      accessorKey: "total.non_taxable_amount",
      header: "Exempted Amt.",
      cell: NACell,
    },
    {
      accessorKey: "total.taxable_amount",
      header: "Taxable Amt.",
      cell: NACell,
    },
    {
      accessorKey: "total.tax_amount",
      header: "VAT",
      cell: NACell,
    },
  ];

  const handleDownloadExcel = async () => {
    try {
      const cleanedParams = Object.fromEntries(
        Object.entries(formValues)
          .filter(
            ([_, value]) =>
              value !== null && value !== undefined && value !== ""
          )
          .map(([key, value]) => [key, String(value)])
      );
      const selectedIds = searchParams.get("selectedItems")?.split(",");
      if (!selectedIds?.length) {
        toast.error("Please select at least one first...");
        return;
      }
      const payload = {
        ...cleanedParams,
        ids: selectedIds,
      };
      await download(
        `${APIS.BILL}purchase-return-excel/`,
        `purchase-return-${moment().format("MMMM-Do-YYYY-h-mm-ss")}.xlsx`,
        `${payload.ids}`
      );
      searchParams.delete("selectedItems");
      setSearchParams(searchParams);
    } catch (e) {
      console.error(e);
    }
  };

  return (
    <>
      <Filter
        register={register}
        errors={errors}
        setValue={setValue}
        getValues={getValues}
        clearErrors={clearErrors}
      />
      <div className={tw`flex justify-end `}>
        <div className={tw`w-24`}>
          <Button label="Download" onClick={() => handleDownloadExcel()} />
        </div>
      </div>
      <DataTable
        columns={columns}
        queryKey={"purchase-return"}
        url={APIS.SALES_PURCHASE_RETURN}
        params={{
          ...formValues,
          type: "Purchase Return",
          is_draft: false,
        }}
        customSearch={true}
        tableName={"purchase-return-register"}
      />
    </>
  );
};
export default PurchaseReturnRegister;

